import Text, {
    TEXT_VARIANT_STYLES,
} from "shared/components/data-display/text/Text";
import styled, { css } from "styled-components";

import { COLOR_VALUES } from "shared/config/colors";
import HStack from "shared/components/layout/stack/HStack";
import VStack from "shared/components/layout/stack/VStack";
import Icon from "shared/components/utils/Icon";
import { SPACING_VALUES } from "shared/config/spacing";
import { mediaQueries } from "shared/config/breakpoints";

export const TimesheetCellHeadDayText = styled(Text)`
    width: max-content;
    border-bottom: 1px solid transparent;

    ${(props) =>
        props.current &&
        css`
            border-bottom-color: ${COLOR_VALUES.Black};
        `}
`;

export const TimesheetGroupRow = styled(HStack)`
    padding-top: 12px;

    ${mediaQueries.small} {
        padding-top: 8px;
    }
`;

export const TimesheetRow = styled(HStack)`
    gap: 0;
    border-bottom: 1px solid ${COLOR_VALUES.Gray_4};
    ${(props) =>
        !props.isClientRow &&
        css`
            &:hover {
                background-color: ${COLOR_VALUES.Gray_8};
            }
        `}
    ${(props) =>
        props.disabledTimeOffRow &&
        css`
            pointer-events: none;
        `}

    @media (min-width: 800px) {
        display: grid;
        grid-template-columns: 27.84fr repeat(7, 10.58fr);

        && > * {
            width: 100%;
            min-width: 0;
        }
    }

    ${mediaQueries.small} {
        display: grid;
        grid-template-columns: 38fr repeat(7, 9fr);
        overflow-x: hidden;

        && > * {
            width: 100%;
            min-width: 0;
        }
    }
`;

export const StickyTimesheetHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${SPACING_VALUES.One};
    box-sizing: border-box;
    position: sticky;
    top: 50px;
    z-index: 10;
    background-color: ${COLOR_VALUES.White};
    padding-top: ${({ $isStuck }) =>
        $isStuck ? SPACING_VALUES.Zero_375 : "0"};
    transition: padding-top 0.15s ease;
`;

export const TimesheetRowHead = styled(HStack)`
    gap: 0;
    border-bottom: 2px solid ${COLOR_VALUES.Gray_2};
    padding-left: calc(0.5rem + 20px);

    @media (min-width: 800px) {
        display: grid;
        grid-template-columns: 27.84fr repeat(7, 10.58fr);

        && > * {
            width: 100%;
            min-width: 0;
        }
    }

    ${mediaQueries.small} {
        display: grid;
        grid-template-columns: 38fr repeat(7, 9fr);
        overflow-x: hidden;
        && > * {
            width: 100%;
            min-width: 0;
            max-width: none;
        }
    }
`;

export const TimesheetClientName = styled(Text)`
    padding-bottom: 12px;
    grid-column: 1 / -1;

    && {
        font-weight: 700;
    }

    ${mediaQueries.small} {
        padding-bottom: 8px;

        && {
            font-size: 0.875rem;
        }
    }
`;

export const TimesheetCell = styled.div`
    width: 10.58%;
    min-width: 0;
    border-left: 1px solid ${COLOR_VALUES.Gray_4};
    padding: 16px 12px;

    ${mediaQueries.small} {
        padding: 8px 4px;
        width: 12.5%;
    }
`;

export const TimesheetCellHead = styled(TimesheetCell)`
    padding: 10px 12px;
    position: relative;

    ${mediaQueries.small} {
        padding: 6px 2px;
        font-size: 0.7rem;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
    }
`;

export const TimesheetCellHeadVStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    ${mediaQueries.small} {
        align-items: center;
        text-align: center;
    }
`;

export const ResponsiveDateContainer = styled.div`
    ${mediaQueries.small} {
        margin-bottom: 0.75rem;

        ${(props) =>
            props.$forceVerticalStack &&
            `
            display: flex;
            flex-direction: column;
            gap: 0.1rem;
            align-items: center;
            text-align: center;
        `}
    }
`;

export const TimesheetProjectNameWrapper = styled(HStack)`
    width: 27.84%;
    min-width: 0;
    padding: 16px 40px 16px 0;

    ${mediaQueries.small} {
        width: 18ch;
        overflow: hidden;
        align-items: center;
        height: auto;
        min-height: 40px;
    }
`;

export const TimesheetCellProjectName = styled.div`
    ${TEXT_VARIANT_STYLES.Body_2_1}
    color: ${COLOR_VALUES.Gray_1};

    ${mediaQueries.small} {
        font-size: 0.75rem;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
`;

export const StyledIcon = styled(Icon)`
    width: 24px;
    height: 24px;

    ${mediaQueries.small} {
        width: 20px;
        height: 20px;
    }
`;

export const TimesheetHeaderSpacer = styled.div`
    flex: 0 0 calc(0.5rem + 20px);

    ${mediaQueries.small} {
        display: none;
    }
`;

export const TimesheetHeaderColumnsWrapper = styled.div`
    display: flex;
    flex: 1;
`;

export const TimesheetCellDropdown = styled.div`
    width: 27.84%;
    min-width: 0;
    padding: 10px 12px 10px 0;

    ${mediaQueries.small} {
        width: 38%;
        min-width: 38%;
        max-width: 38%;
        padding: 8px 6px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

export const TimesheetClickableWrapper = styled.a`
    padding: 0;
    cursor: pointer;
`;

export const TimesheetCellHeadTotalText = styled(Text)`
    ${mediaQueries.small} {
        font-size: 0.85rem;
    }
`;

export const TimesheetGridContainer = styled(VStack)`
    @media (max-width: 799px) {
        overflow-x: clip;
    }

    &:has(${TimesheetRow} > *:nth-child(2):hover)
        ${TimesheetCellHead}:nth-child(2),
    &:has(${TimesheetRow} > *:nth-child(3):hover)
        ${TimesheetCellHead}:nth-child(3),
    &:has(${TimesheetRow} > *:nth-child(4):hover)
        ${TimesheetCellHead}:nth-child(4),
    &:has(${TimesheetRow} > *:nth-child(5):hover)
        ${TimesheetCellHead}:nth-child(5),
    &:has(${TimesheetRow} > *:nth-child(6):hover)
        ${TimesheetCellHead}:nth-child(6),
    &:has(${TimesheetRow} > *:nth-child(7):hover)
        ${TimesheetCellHead}:nth-child(7),
    &:has(${TimesheetRow} > *:nth-child(8):hover)
        ${TimesheetCellHead}:nth-child(8) {
        background-color: ${COLOR_VALUES.Gray_8};
    }
`;
